import { InjectionToken } from '@angular/core';
import zipObject from 'lodash/zipObject';

// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ContentBundleConfig } from '@cigna/resource-content/data-access';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { AppContext } from '@cigna/chcp/shared/util';

export const APP_CONTEXT = new InjectionToken<AppContext>('appContext');

export const ENVIRONMENT_CONFIG = new InjectionToken<EnvironmentConfig>(
  'environmentConfig',
);

export interface EnvironmentConfig {
  production: boolean;
  oidc: {
    clientId: string;
    redirectUri: string;
    metadataUri: string;
    authorizeUri: string;
    secureUris: string[];
    noRefreshUris: string[];
    publicUris: string[];
  };
  redirect: {
    crossOrigin: {
      domain: string;
    };
    mhk?: string;
    hsConnect?: string;
    inbox?: string;
  };
  omni?: {
    sonicNotification: string;
    identityUrl?: string;
    botUrl?: string;
    liveUrl?: string;
    url?: string;
    publicUrl?: string;
  };
  api: {
    gateways: {
      cdn: string;
      resourceContent: string;
      secureApi: string;
      publicApi: string;
      gsgPublicApi: string;
      gsgSecureApi: string;
      graphql: string;
    };
    proxy: {
      headers: {
        [header: string]: string;
      };
    };
    searchIndex: string;
    salesDemo: boolean;
  };
  contentBundle: ContentBundleConfig;
  sessionTimeoutPopup: {
    timeoutDuration: number;
    logoutDuration: number;
    keepAliveUrl: string;
  };
  trustId: string;
  features: FeatureToggles;
}

export const environmentDefaults = {
  production: true,
  sessionTimeoutPopup: {
    timeoutDuration: 25,
    logoutDuration: 5,
    keepAliveUrl: '/app/assets/images/isam-keepalive.gif',
  },
  trustId: '504516',
  contentBundle: {
    manifestFilename: `content/chcp/manifest.json?t=${Date.now().toString()}`,
    resolvePageContentTimeoutSeconds: 1,
  },
};

export const environmentDefaultsEvernorth = {
  ...environmentDefaults,
  contentBundle: {
    manifestFilename: `content/evernorth-provider/manifest.json?t=${Date.now().toString()}`,
    resolvePageContentTimeoutSeconds: 1,
  },
};

export const oidcDefault = {
  clientId: 'chcp_spa_client',
  redirectUri: '/app/auth_callback.html',
  metadataUri: '/mga/sps/oauth/oauth20/metadata/chcp_spa_def',
  authorizeUri: '/mga/sps/oauth/oauth20/authorize',
  publicUris: [],
  noRefreshUris: [],
};

export const oidcEvernorthDefault = {
  clientId: 'evernorth_provider_spa_client',
  redirectUri: '/app/auth_callback.html',
  metadataUri: '/mga/sps/oauth/oauth20/metadata/evernorth_provider_spa_def',
  authorizeUri: '/mga/sps/oauth/oauth20/authorize',
  publicUris: [],
  noRefreshUris: [],
};

export const CDN_BASE_URL = new InjectionToken<string>('CDN_BASE_URL');

export const featureFlags = [
  'nextRelease', // dont remove me :)
  'trust',
  'patientOldView',
  'patientEnhBehaviorEmbedded',
  'patientEnhBehaviorStandalone',
  'patientEnhPower',
  'globalBenEnh',
  'messageCenter',
  'claimsMcNotification',
  'messageCenterPreference',
  'messageCenterPrefWAM',
  'messageCenterDashboard',
  'messageCenterFilters',
  'messageCenterClaimStatus',
  'precertManual',
  'precertDg',
  'pdlViewer',
  'newFeaturesBanner',
  'emailVerifyPopup',
  'claim360GA',
  'claim360SearchOffice',
  'enablePreferredLOB',
  'mobilePhoneVerification',
  'allowTinAddition',
  'allowTempIdPassAddition',
  'gqlDashboard',
  'gqlPatientDetails',
  'gqlClaimDetails',
  'manageAccessTilePagination',
  'pdufAwsOperations',
  'findLOBAwsOperations',
  'createUserAwsOperations',
  'eftMedicalAws',
  'eftCombinedDental',
  'dentalLimitationAndProcedureEnh',
  'cptFavoriteListEnh',
  'canUpdateAnalyticsData',
  'loginUserProfileAWS',
  'enableCOCE',
];

export type Feature = typeof featureFlags[number];

export type FeatureToggles = Record<Feature, boolean>;

export const featureFlagDefaults: FeatureToggles = zipObject(
  featureFlags,
  Array(featureFlags.length).fill(true),
);
